import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Typography, Alert } from '@mui/material';

// Import the real Stripe components
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';



/**
 * Server error codes whose `message` is already addressed to the shopper and
 * tells them exactly what to do next (usually: email us and we will place the
 * order by hand). Anything else is a fault, not a rule, and gets the generic
 * prefix.
 */
const SHOPPER_FACING_ERROR_CODES = new Set([
  'below_minimum_charge',
  'above_maximum_charge',
  'quantity_above_maximum',
]);

const StripePayment = ({ amount, shippingCost, shippingOptionId, discountCode, onSuccess, onError, onPricingLoaded, customerInfo, shippingInfo, orderItems }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  // Server-computed charge total (subtotal + shipping + tax) from create-payment-intent.
  // The `amount` prop is only the cart subtotal, so the button must not display it once
  // the real total is known — the label has to match what the card is actually charged.
  const [serverTotalCents, setServerTotalCents] = useState(null);
  
  // Use our mock Stripe hooks
  const stripe = useStripe();
  const elements = useElements();

  // The customerInfo/shippingInfo/orderItems props are new object identities on
  // every parent render, so this effect would otherwise re-fire per render —
  // each firing creates another pending order row and Stripe PaymentIntent.
  // One intent per mount: the component unmounts if the user goes back and
  // changes shipping, so a fresh mount is the only time inputs can change.
  const intentRequested = useRef(false);

  // Create a payment intent when the component mounts
  useEffect(() => {
    const getPaymentIntent = async () => {
      try {
        // Prepare customer and shipping data
        const customer = customerInfo || {};
        const shipping = shippingInfo || {};
        
        // Call our local server to create a payment intent
        const response = await fetch(`${process.env.REACT_APP_API_URL}/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // The server resolves the authoritative price from this id; the
            // cost below is sent for logging/parity only and is not trusted.
            shippingOptionId: shippingOptionId || 'standard',
            shippingCost: shippingCost || 0,
            discountCode: discountCode || null,
            customer: {
              email: customer.email,
              name: customer.firstName && customer.lastName ? 
                `${customer.firstName} ${customer.lastName}` : 
                (shipping.name || 'Guest Customer'),
              phone: customer.phone
            },
            shipping: {
              name: shipping.name || 
                (customer.firstName && customer.lastName ? 
                  `${customer.firstName} ${customer.lastName}` : 'Guest Customer'),
              address1: shipping.address1,
              address2: shipping.address2,
              city: shipping.city,
              state: shipping.state,
              zip: shipping.zip,
              country: shipping.country || 'US'
            },
            orderItems: orderItems ? orderItems.map(item => ({
              ...item,
              // Ensure personalization text is included in order data
              personalizationText: item.personalizationText || ''
            })) : [],
            metadata: {
              // Add any additional metadata here
              customer_name: customer.firstName && customer.lastName ? 
                `${customer.firstName} ${customer.lastName}` : 'Guest Customer',
            }
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          const err = new Error(errorData.error || 'Failed to create payment intent');
          // A total under Stripe's $0.50 floor (deep discount) is a complete,
          // actionable sentence on its own — don't bury it behind our generic
          // "Failed to create payment intent" prefix.
          err.code = errorData.code;
          throw err;
        }
        
        const data = await response.json();
        setClientSecret(data.clientSecret);

        // Store the order ID if provided
        if (data.orderId) {
          localStorage.setItem('currentOrderId', data.orderId);
        }

        // Notify parent of server-calculated pricing
        if (data.totalCents != null) {
          setServerTotalCents(data.totalCents);
        }
        if (onPricingLoaded && data.taxAmountCents != null && data.totalCents != null) {
          onPricingLoaded({
            taxAmountCents: data.taxAmountCents,
            totalCents: data.totalCents,
            shippingCents: data.shippingCents,
            shippingMethodName: data.shippingMethodName,
            discountCents: data.discountCents,
          });
        }
      } catch (err) {
        console.error('Payment intent error:', err);
        // These codes carry a message written for the shopper — what is wrong
        // and what to do about it. Prefixing them with "Failed to create
        // payment intent" would bury the useful sentence under a stack-trace
        // voice, so they pass through verbatim.
        setError(
          SHOPPER_FACING_ERROR_CODES.has(err.code)
            ? err.message
            : 'Failed to create payment intent. ' + err.message,
        );
        if (onError) onError(err);
      }
    };
    
    if ((amount > 0 || shippingCost != null) && !intentRequested.current) {
      intentRequested.current = true;
      getPaymentIntent();
    }
  }, [amount, shippingCost, customerInfo, shippingInfo, orderItems, onError, onPricingLoaded]);
  
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      setError('Stripe has not been properly initialized');
      return;
    }
    
    if (!cardComplete) {
      setError('Please enter complete card information');
      return;
    }
    
    setProcessing(true);
    setError(null);
    
    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('Card element not found');
      }
      
      // Confirm the payment with Stripe
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });
      
      if (result.error) {
        // Show error to your customer
        throw new Error(result.error.message);
      } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        onSuccess(result.paymentIntent.id);
      } else {
        // Payment status is not succeeded
        throw new Error('Payment was not successful. Status: ' + 
          (result.paymentIntent ? result.paymentIntent.status : 'unknown'));
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred while processing your payment');
      if (onError) onError(err);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    setError(event.error ? event.error.message : '');
  };
  
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Secured by Stripe. We never store your card details.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Card Details
      </Typography>
      
      <Box sx={{ mb: 3, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
        <CardElement 
          id="card-element"
          onChange={handleCardChange}
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={processing || !cardComplete || !stripe || !clientSecret}
        fullWidth
        sx={{ mt: 2 }}
      >
        {processing ? (
          <CircularProgress size={24} />
        ) : (
          `Pay $${((serverTotalCents ?? amount) / 100).toFixed(2)}`
        )}
      </Button>
      
    </Box>
  );
};

StripePayment.propTypes = {
  amount: PropTypes.number.isRequired,
  shippingCost: PropTypes.number,
  shippingOptionId: PropTypes.string,
  discountCode: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onPricingLoaded: PropTypes.func,
  customerInfo: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string
  }).isRequired,
  shippingInfo: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string
  }).isRequired,
  orderItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      quantity: PropTypes.number,
      options: PropTypes.object,
      personalization: PropTypes.string
    })
  ).isRequired
};

export default StripePayment;
